import React from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { IconCaretDownSmall } from "~/components/icon";
import { cx } from "~/tools/class-variance";
import { prop } from "~/tools/prop";
import { useHandler } from "~/tools/use-handler";

import { RESET_VIEW_SHORTCUT, SAVE_AS_VIEW_SHORTCUT, SAVE_VIEW_SHORTCUT } from "../../../app/Shortcuts";
import { DropdownWithHost } from "../../dropdown-with-host";
import { CtxMenu } from "../CtxMenu/CtxMenu";
import { MenuItem } from "../menu/Menu";
import { Tooltip } from "../tooltip/Tooltip";

export interface SaveAsButtonAction<T = unknown> {
	/**
	 * A value used to determine when an action is visible.
	 * When truthy - the action is visible and the value passed to the {@link handler},
	 * otherwise the action is not hidden.
	 */
	enabler: false | null | undefined | T;
	title: string;
	handler: (value: T) => void;
	isPrimary?: boolean;
}

export interface SaveButtonProps {
	save: SaveAsButtonAction<unknown>;
	saveAs: SaveAsButtonAction<unknown>;
	reset: SaveAsButtonAction<unknown>;
	rest?: ReadonlyArray<SaveAsButtonAction<unknown>>;
}

export const SaveButton: React.FC<SaveButtonProps> = ({ save, saveAs, reset, rest = [] }) => {
	useHotkeys(
		SAVE_VIEW_SHORTCUT,
		useHandler(() => save.handler(save.enabler)),
		{ enabled: Boolean(save.enabler) },
	);
	useHotkeys(
		SAVE_AS_VIEW_SHORTCUT,
		useHandler(() => saveAs.handler(saveAs.enabler)),
		{ enabled: Boolean(saveAs.enabler) },
	);
	useHotkeys(
		RESET_VIEW_SHORTCUT,
		useHandler(() => reset.handler(reset.enabler)),
		{ enabled: Boolean(reset.enabler) },
	);

	const actions = [
		{ ...save, hotkey: SAVE_VIEW_SHORTCUT },
		{ ...saveAs, hotkey: SAVE_AS_VIEW_SHORTCUT },
		{ ...reset, hotkey: RESET_VIEW_SHORTCUT },
		...rest.map((action) => ({ ...action, hotkey: null })),
	].filter(prop("enabler"));

	const firstAction = actions.at(0);

	if (firstAction == null) {
		return null;
	}

	return (
		<DropdownWithHost
			align={{ offset: [0, 3] }}
			overlay={
				<CtxMenu
					className="grid grid-cols-[auto_auto] items-center gap-x-6 *:*:contents *:contents"
					data-testid="save-as-button"
				>
					{actions.map((action) => (
						<MenuItem key={action.title} onClick={() => action.handler(action.enabler)}>
							<div className="col-span-full grid grid-cols-subgrid">
								{action.title}

								{action.hotkey && (
									<span className="place-self-end text-11 text-grey-500 uppercase">{action.hotkey}</span>
								)}
							</div>
						</MenuItem>
					))}
				</CtxMenu>
			}
			placement="bottomLeft"
			trigger={["click"]}
			triggerElement={
				<button
					className={cx(
						"top-nav-button top-nav-button-skinny rounded-l-none",
						firstAction.isPrimary && "top-nav-button-highlighted",
					)}
					type="button"
				>
					<IconCaretDownSmall aria-label="caret-down" width="7" />
				</button>
			}
			type={firstAction.isPrimary ? "primary" : "default"}
			visible={actions.length > 1}
		>
			<Tooltip
				title={<div className="text-xs uppercase opacity-75">{firstAction.hotkey}</div>}
				visible={firstAction.hotkey != null}
			>
				<button
					className={cx(
						"top-nav-button",
						firstAction.isPrimary && "top-nav-button-highlighted",
						actions.length > 1 && "rounded-r-none",
					)}
					data-testid="save-button"
					onClick={() => firstAction.handler(firstAction.enabler)}
					type="button"
				>
					{firstAction.title}
				</button>
			</Tooltip>
		</DropdownWithHost>
	);
};
